import { useState } from "react";
import type { DragEvent } from "react";
import { reorderIds } from "@/lib/reorderStages";
import { useDragSessionGuard } from "@/hooks/useDragSessionGuard";

// Shared native HTML5 drag-and-drop wiring for reordering stage rows. Used by
// both the schedule creator (CrewSchedule) and the read-only Schedule page so
// the interaction + the persisted result stay identical.
//
// Only the stage LABEL cell is the drag handle AND the drop target — the
// timeline grid keeps its own drop behaviour (crew/assignment drops in the
// creator), so stage reordering never conflicts with assigning crew.

export interface StageReorderApi {
  draggingId: string | null;
  overId: string | null;
  /** Spread onto a stage label cell to make it draggable + a drop target. */
  getStageProps: (stageId: string) => {
    draggable: true;
    onDragStart: (e: DragEvent) => void;
    onDragEnd: () => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
}

/**
 * @param orderedIds the FULL day-ordered list of stage ids (including stages
 *   with no artists, so display_order stays a clean 0..n-1 permutation).
 * @param onReorder called with the new full id order when a drop changes it.
 */
export function useStageReorder(
  orderedIds: string[],
  onReorder: (newOrderedIds: string[]) => void,
): StageReorderApi {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const endDrag = () => {
    setDraggingId(null);
    setOverId(null);
  };

  // A browser that abandons a drag without firing `dragend` would otherwise
  // leave the label column stuck in its dragging look, and every later
  // dragover would keep treating a finished drag as live.
  useDragSessionGuard(draggingId !== null, endDrag);

  const getStageProps = (stageId: string) => ({
    draggable: true as const,
    onDragStart: (e: DragEvent) => {
      setDraggingId(stageId);
      e.dataTransfer.effectAllowed = "move";
      // Firefox requires data to be set for a drag to actually start.
      e.dataTransfer.setData("text/plain", stageId);
    },
    onDragEnd: endDrag,
    onDragOver: (e: DragEvent) => {
      if (!draggingId || draggingId === stageId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOverId(stageId);
    },
    onDragLeave: () => {
      setOverId((cur) => (cur === stageId ? null : cur));
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (draggingId && draggingId !== stageId) {
        onReorder(reorderIds(orderedIds, draggingId, stageId));
      }
      endDrag();
    },
  });

  return { draggingId, overId, getStageProps };
}
